import { getAiDetectionApiBaseUrl } from '../api/client.ts';
import { getAuthToken } from "./apiClient";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";
import { tokenManager } from "../utils/tokenManager";


/**
 * AI alert service base (use cases, cameras, zones).
 * Prefer REACT_APP_API_URL_FOR_AI_DETECTION; fall back to REACT_APP_API_URL.
 */
function getAiAlertServiceBaseUrl() {
  const raw =
    getAiDetectionApiBaseUrl() ||
    process.env.REACT_APP_API_URL ||
    "";
  return String(raw).trim().replace(/\/$/, "");
}

/**
 * Use cases API (GET /use-cases/) — list models for Alert Type in Add Rule.
 */
export const useCasesAPI = {
  /**
   * @param {{ user_id?: string|null }} [params] - Omit user_id for admin list; set to filter by assignment.
   * @returns {Promise<Object>} Raw JSON (e.g. { message, status, data: [...] }).
   */
  list: async (params = {}) => {
    const base = getAiAlertServiceBaseUrl();
    if (!base) {
      const err = new Error(
        "API URL is not configured. Set REACT_APP_API_URL or REACT_APP_API_URL_FOR_AI_DETECTION.",
      );
      err.code = "MISSING_USE_CASES_BASE_URL";
      throw err;
    }
    const query = new URLSearchParams();
    if (
      params.user_id != null &&
      String(params.user_id).trim() !== "" &&
      params.user_id !== "null"
    ) {
      query.set("user_id", String(params.user_id).trim());
    }
    const qs = query.toString();
    const url = `${base}/use-cases/${qs ? `?${qs}` : ""}`;
    const token = getAuthToken();
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...userTimezoneHeaders(),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (process.env.NODE_ENV === "development") {
        console.error("useCasesAPI.list failed:", {
          status: response.status,
          error: errorData,
          url,
        });
      }
      if (errorData.loggedIn === false) {
        tokenManager.clearAuth();
        window.location.href = "/login";
        return;
      }
      const error = new Error(
        errorData.message ||
          errorData.detail ||
          `HTTP error! status: ${response.status}`,
      );
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    return response.json();
  },
};


async function aiAlertServiceFetchJson(pathWithLeadingSlash, options = {}) {
  const base = getAiAlertServiceBaseUrl();
  if (!base) {
    const err = new Error(
      "API URL is not configured. Set REACT_APP_API_URL or REACT_APP_API_URL_FOR_AI_DETECTION.",
    );
    err.code = "MISSING_AI_ALERT_BASE_URL";
    throw err;
  }
  const url = `${base}${pathWithLeadingSlash.startsWith("/") ? pathWithLeadingSlash : `/${pathWithLeadingSlash}`}`;
  const token = getAuthToken();
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
      ...(options.headers && typeof options.headers === "object"
        ? options.headers
        : {}),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (process.env.NODE_ENV === "development") {
      console.error("aiAlertServiceFetchJson failed:", {
        status: response.status,
        error: errorData,
        url,
      });
    }
    if (errorData.loggedIn === false) {
      tokenManager.clearAuth();
      window.location.href = "/login";
      return;
    }
    const error = new Error(
      errorData.message ||
        errorData.detail ||
        `HTTP error! status: ${response.status}`,
    );
    error.status = response.status;
    error.data = errorData;
    throw error;
  }

  return response.json();
}

/**
 * Cameras & zones on the AI alert service (GET /cameras, GET /cameras/:id/zones).
 */
export const camerasAPI = {
  /**
   * @param {{ is_active?: boolean }} [params] - Optional filter (omit for all cameras).
   */
  list: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.is_active === true || params.is_active === false) {
      query.set("is_active", String(params.is_active));
    }
    const qs = query.toString();
    const path = `/cameras${qs ? `?${qs}` : ""}`;
    return aiAlertServiceFetchJson(path);
  },

  /**
   * @param {string} cameraId - Camera UUID
   */
  listZones: async (cameraId) => {
    const id = encodeURIComponent(String(cameraId));
    return aiAlertServiceFetchJson(`/cameras/${id}/zones`);
  },
};


/**
 * Zones on the AI alert service (GET /zones).
 */
export const zonesAPI = {
  /**
   * List zones.
   * @returns {Promise<Object>} Raw JSON response (array or { data: [...] }).
   */
  list: async () => {
    return aiAlertServiceFetchJson("/zones");
  },
};
